import React, { useCallback, useEffect, useMemo, useState } from "react";

import BasicCRUD from "../../core/basic-crud";
import AppContext from "../../../app-context";
import { socket, requestData } from "../../../socket";

const InvitationsTab = ({
    t,
    user,
    inviteAddDisableConfig,
    inviteDialogTrigger,
    onDialogHandled,
    invitesObject,
    itemsPerPage,
    isMemberRole,
}) => {
    const [inviteFieldsData, setInviteFieldsData] = useState({});
    const [inviteResponse, setInviteResponse] = useState({});
    const [invitesAlert, setInvitesAlert] = useState({ type: "", text: "" });
    const [inviteStatusFilter, setInviteStatusFilter] = useState("all");
    const inviteStatusOptions = useMemo(
        () => ["all", "pending", "accepted", "expired", "revoked"],
        []
    );

    const invitationStatusMap = useMemo(
        () => ({
            pending: t.inviteStatusPending,
            accepted: t.inviteStatusAccepted,
            expired: t.inviteStatusExpired,
            revoked: t.inviteStatusRevoked,
        }),
        [t]
    );

    const getInvitationStatusLabel = (status) => invitationStatusMap[status] || status || t.na;

    const getInvitationStatusClass = (status) => {
        switch ((status || "").toLowerCase()) {
            case "accepted":
                return "badge bg-success-subtle text-success";
            case "expired":
                return "badge bg-secondary-subtle text-secondary";
            case "revoked":
                return "badge bg-danger-subtle text-danger";
            default:
                return "badge bg-warning-subtle text-warning";
        }
    };

    const formatDateTime = (value) => {
        if (!value) return t.na;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleString();
    };

    const inviteRoleOptions = useMemo(
        () => [
            { id: "admin", name: t.roleAdmin },
            { id: "member", name: t.roleMember },
        ],
        [t]
    );

    const inviteFormFields = useMemo(() => ({
        add: [
            {
                type: "tab",
                name: t.tabMain,
                image: "",
                fields: [
                    [
                        { label: t.inviteFirstNameLabel, key: "first_name", showLabel: "top", default_value: "" },
                        { label: t.inviteLastNameLabel, key: "last_name", showLabel: "top", default_value: "" },
                    ],
                    [
                        { label: t.inviteEmailLabel, key: "email", showLabel: "top", default_value: "" },
                    ],
                    [
                        {
                            label: t.inviteRoleLabel,
                            key: "role",
                            type: "selectbox",
                            data: inviteRoleOptions,
                            default_value: "member",
                            showLabel: "top",
                        },
                    ],
                ],
            },
        ],
    }), [t, inviteRoleOptions]);

    const inviteColumns = useMemo(() => [
        {
            label: t.inviteColEmail,
            key: "email",
            format: (row) => (
                <div>
                    <div className="fw-semibold">{row.email || t.na}</div>
                    {row.first_name || row.last_name ? (
                        <div className="text-muted small">
                            {[row.first_name, row.last_name].filter(Boolean).join(" ") || t.na}
                        </div>
                    ) : null}
                </div>
            ),
        },
        { label: t.inviteColRole, key: "role", format: (row) => row.role || t.na },
        {
            label: t.inviteColStatus,
            key: "status",
            format: (row) => (
                <span className={getInvitationStatusClass(row.status)}>
                    {getInvitationStatusLabel(row.status)}
                </span>
            ),
        },
        { label: t.inviteColSent, key: "sent_at", format: (row) => formatDateTime(row.sent_at) },
        { label: t.inviteColExpires, key: "expires_at", format: (row) => formatDateTime(row.expires_at) },
        {
            label: t.inviteColInvitedBy,
            key: "invited_by_name",
            format: (row) => (
                <div>
                    <div className="fw-semibold">{row.invited_by_name || t.na}</div>
                    {row.invited_by_email && (
                        <div className="text-muted small">{row.invited_by_email}</div>
                    )}
                </div>
            ),
        },
    ], [t]);

    const isPendingInvite = useCallback(
        (invite) => (invite?.status || "").toLowerCase() === "pending",
        []
    );

    const inviteActions = useMemo(() => [
        {
            name: "resend",
            label: t.inviteResend,
            icon: "ti ti-mail-forward",
            fn: (_name, row) => !isMemberRole && isPendingInvite(row),
        },
        {
            name: "cancel",
            label: t.inviteCancel,
            icon: "ti ti-ban",
            fn: (_name, row) => !isMemberRole && isPendingInvite(row),
        },
    ], [t, isPendingInvite, isMemberRole]);

    const refreshInvites = useCallback(
        (pageOverride = null) => {
            if (!user?.token) return;
            const page = pageOverride ?? (AppContext.pages?.[invitesObject]?.pageNum || 1);
            requestData(
                user,
                invitesObject,
                { page, items_count: itemsPerPage, status: inviteStatusFilter },
                "list"
            );
        },
        [user, invitesObject, itemsPerPage, inviteStatusFilter]
    );

    const handleInvitationAction = (action, invite) => {
        if (!user?.token || !invite?.id || isMemberRole) return;
        setInvitesAlert({ type: "", text: "" });
        socket.emit(`user_invitations:${action}`, {
            token: user.token,
            invitation_id: invite.id,
        });
    };

    useEffect(() => {
        const handleResend = (payload = {}) => {
            if (payload.status === 1) {
                setInvitesAlert({ type: "success", text: t.inviteResendSuccess });
                refreshInvites();
            } else {
                setInvitesAlert({ type: "error", text: payload?.err?.message || t.inviteActionError });
            }
        };
        const handleCancel = (payload = {}) => {
            if (payload.status === 1) {
                setInvitesAlert({ type: "success", text: t.inviteCancelSuccess });
                refreshInvites();
            } else {
                setInvitesAlert({ type: "error", text: payload?.err?.message || t.inviteActionError });
            }
        };

        socket.on("user_invitations:resend-response", handleResend);
        socket.on("user_invitations:cancel-response", handleCancel);
        return () => {
            socket.off("user_invitations:resend-response", handleResend);
            socket.off("user_invitations:cancel-response", handleCancel);
        };
    }, [t, refreshInvites]);

    useEffect(() => {
        if (!inviteResponse || typeof inviteResponse.status === "undefined") return;
        if (inviteResponse.status === 1 && inviteResponse.created === 1) {
            setInvitesAlert({ type: "success", text: t.inviteCreatedOk });
            refreshInvites();
        } else if (inviteResponse.status === 0 && inviteResponse.err) {
            setInvitesAlert({ type: "error", text: inviteResponse.err.message || t.inviteActionError });
        }
    }, [inviteResponse, t, refreshInvites]);

    useEffect(() => {
        AppContext.pages = AppContext.pages || {};
        AppContext.pages[invitesObject] = { ...(AppContext.pages[invitesObject] || {}), pageNum: 1 };
        refreshInvites(1);
    }, [inviteStatusFilter, invitesObject, refreshInvites]);

    useEffect(() => {
        refreshInvites();
    }, [refreshInvites]);

    const renderInviteCard = (item) => {
        const pending = isPendingInvite(item);
        return (
            <div className="card h-100 shadow-sm border">
                <div className="card-body d-flex flex-column">
                    <div className="mb-2">
                        <div className="text-muted text-uppercase fw-semibold fs-2 mb-1">{t.inviteColEmail}</div>
                        <div className="fw-semibold">{item.email || t.na}</div>
                    </div>
                    <div className="mb-2">
                        <div className="text-muted small mb-1">{t.inviteColRole}</div>
                        <div className="fw-semibold text-capitalize">{item.role || t.na}</div>
                    </div>
                    <div className="mb-2">
                        <div className="text-muted small mb-1">{t.inviteColStatus}</div>
                        <span className={getInvitationStatusClass(item.status)}>
                            {getInvitationStatusLabel(item.status)}
                        </span>
                    </div>
                    <div className="mb-2">
                        <div className="text-muted small mb-1">{t.inviteColSent}</div>
                        <div className="fw-semibold">{formatDateTime(item.sent_at)}</div>
                    </div>
                    <div className="mb-2">
                        <div className="text-muted small mb-1">{t.inviteColExpires}</div>
                        <div className="fw-semibold">{formatDateTime(item.expires_at)}</div>
                    </div>
                    <div className="mb-3">
                        <div className="text-muted small mb-1">{t.inviteColInvitedBy}</div>
                        <div className="fw-semibold">{item.invited_by_name || t.na}</div>
                        {item.invited_by_email && <div className="text-muted small">{item.invited_by_email}</div>}
                    </div>

                    <div className="mt-auto d-flex gap-2 justify-content-end">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={!pending || isMemberRole}
                            onClick={() => handleInvitationAction("resend", item)}
                        >
                            {t.inviteResend}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            disabled={!pending || isMemberRole}
                            onClick={() => handleInvitationAction("cancel", item)}
                        >
                            {t.inviteCancel}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <BasicCRUD
            key={`invites-${inviteStatusFilter}`}
            title={t.invitesTitle}
            subTitle={t.invitesSubtitle}
            homeURL={t.homeURL}
            object={invitesObject}
            addTitle={t.inviteAddTitle}
            addButtonWrapperClass="col-12 col-xl-3 d-flex justify-content-center justify-content-xl-end mt-2 mt-xl-0"
            addButtonWrapperStyle={{ height: "100%" }}
            columns={inviteColumns}
            disable={inviteAddDisableConfig || undefined}
            fields={inviteFormFields}
            actions={inviteActions}
            fieldsData={inviteFieldsData}
            setFieldsData={setInviteFieldsData}
            response={inviteResponse}
            setResponse={setInviteResponse}
            ownHandler={true}
            externalAddTrigger={inviteDialogTrigger}
            onExternalAddHandled={onDialogHandled}
            hideGroupActions={true}
            viewModes={["table", "blocks"]}
            defaultViewMode="table"
            renderItem={renderInviteCard}
            cardLayout={{ columnClass: "col-12 col-md-6 col-xxl-4" }}
            args={{ items_count: itemsPerPage, status: inviteStatusFilter }}
            filters={{ status: inviteStatusFilter }}
            searchFields={{
                simple: [
                    {
                        type: "tab",
                        name: "",
                        fields: [
                            [
                                {
                                    type: "custom",
                                    fn: () => (
                                        <div className="mb-2">
                                            <label className="form-label fw-semibold">{t.inviteColStatus}</label>
                                            <select
                                                className="form-select"
                                                value={inviteStatusFilter}
                                                onChange={(event) => setInviteStatusFilter(event.target.value)}
                                            >
                                                {inviteStatusOptions.map((status) => (
                                                    <option key={status} value={status}>
                                                        {status === "all" ? t.filterAll : invitationStatusMap[status]}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    ),
                                },
                            ],
                        ],
                    },
                ],
            }}
            disableSaveFilters={true}
            tableAction={handleInvitationAction}
        >
            {invitesAlert.text ? (
                <div className={`alert alert-${invitesAlert.type === "error" ? "danger" : "success"} mb-3`}>
                    {invitesAlert.text}
                </div>
            ) : null}
        </BasicCRUD>
    );
};

export default InvitationsTab;
